import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarProvider, useSidebar } from '../../components/ui/Sidebar';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import MetricCard from './components/MetricCard';
import RecentContentTable from './components/RecentContentTable';

import ActivityFeed from './components/ActivityFeed';
import PerformanceChart from './components/PerformanceChart';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const DashboardContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { user, loading } = useAuth();
  const [metrics, setMetrics] = useState({
    totalEvents: 0,
    publishedEvents: 0,
    pendingReview: 0,
    totalRegistrations: 0,
  });
  const [recentEvents, setRecentEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    setDataLoading(true);
    setError(null);
    try {
      const [
        totalEventsRes,
        publishedEventsRes,
        pendingReviewRes,
        totalRegistrationsRes,
        recentEventsRes,
        recentActivitiesRes,
      ] = await Promise.all([
        supabase?.from('events')?.select('id', { count: 'exact', head: true }),
        supabase?.from('events')?.select('id', { count: 'exact', head: true })?.eq('is_published', true),
        supabase?.from('events')?.select('id', { count: 'exact', head: true })?.eq('status', 'pending_review'),
        supabase?.from('event_registrations')?.select('id', { count: 'exact', head: true }),
        supabase?.from('events')?.select('id, title, category, status, is_published, created_at, updated_at, event_host_profiles(full_name, organization_name)')?.order('updated_at', { ascending: false })?.limit(5),
        supabase?.from('events')?.select('id, title, status, is_published, created_at, updated_at, event_host_profiles(full_name, organization_name)')?.order('created_at', { ascending: false })?.limit(8),
      ]);

      setMetrics({
        totalEvents: totalEventsRes?.count ?? 0,
        publishedEvents: publishedEventsRes?.count ?? 0,
        pendingReview: pendingReviewRes?.count ?? 0,
        totalRegistrations: totalRegistrationsRes?.count ?? 0,
      });

      if (recentEventsRes?.data) {
        setRecentEvents(recentEventsRes?.data?.map((ev) => ({
          id: ev?.id,
          title: ev?.title,
          category: ev?.category,
          author: ev?.event_host_profiles?.organization_name || ev?.event_host_profiles?.full_name || 'Unknown Host',
          status: ev?.is_published ? 'published' : (ev?.status === 'pending_review' ? 'pending' : ev?.status === 'rejected' ? 'archived' : 'draft'),
          lastModified: ev?.updated_at || ev?.created_at,
          icon: 'Calendar',
          iconColor: 'bg-primary/10 text-primary',
        })));
      }

      if (recentActivitiesRes?.data) {
        setActivities(recentActivitiesRes?.data?.map((ev) => {
          const hostName = ev?.event_host_profiles?.organization_name || ev?.event_host_profiles?.full_name || 'A host';
          let type = 'edit';
          let action = 'updated event';
          if (ev?.is_published) {
            type = 'publish';
            action = 'published event';
          } else if (ev?.status === 'pending_review') {
            type = 'approval';
            action = 'submitted event for review';
          } else if (ev?.status === 'rejected') {
            type = 'delete';
            action = 'had event rejected';
          } else {
            type = 'upload';
            action = 'created new event';
          }
          return {
            id: ev?.id,
            type,
            user: hostName,
            action,
            target: ev?.title,
            timestamp: ev?.created_at,
          };
        }));
      }

      // Build performance chart: registrations per day for last 7 days
      const days = 7;
      const dateLabels = [];
      const now = new Date();
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(now);
        d?.setDate(d?.getDate() - i);
        dateLabels?.push({
          label: d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          dateStr: d?.toISOString()?.split('T')?.[0],
        });
      }

      const startDate = new Date(now);
      startDate?.setDate(startDate?.getDate() - (days - 1));
      startDate?.setHours(0, 0, 0, 0);

      const [regRes, eventsCreatedRes] = await Promise.all([
        supabase?.from('event_registrations')?.select('registered_at')?.gte('registered_at', startDate?.toISOString()),
        supabase?.from('events')?.select('created_at')?.gte('created_at', startDate?.toISOString()),
      ]);

      const regByDay = {};
      const evByDay = {};
      dateLabels?.forEach(({ dateStr }) => {
        regByDay[dateStr] = 0;
        evByDay[dateStr] = 0;
      });

      regRes?.data?.forEach((r) => {
        const d = r?.registered_at?.split('T')?.[0];
        if (d && regByDay?.[d] !== undefined) regByDay[d]++;
      });

      eventsCreatedRes?.data?.forEach((e) => {
        const d = e?.created_at?.split('T')?.[0];
        if (d && evByDay?.[d] !== undefined) evByDay[d]++;
      });

      setPerformanceData(
        dateLabels?.map(({ label, dateStr }) => ({
          date: label,
          registrations: regByDay?.[dateStr],
          events: evByDay?.[dateStr],
        }))
      );
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [navigate, user, loading]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user, fetchDashboardData]);

  const metricsData = [
    {
      title: 'Total Events',
      value: dataLoading ? '—' : metrics?.totalEvents?.toLocaleString(),
      icon: 'Calendar',
      iconColor: 'bg-primary/10 text-primary',
    },
    {
      title: 'Published Events',
      value: dataLoading ? '—' : metrics?.publishedEvents?.toLocaleString(),
      icon: 'CheckCircle2',
      iconColor: 'bg-success/10 text-success',
    },
    {
      title: 'Pending Review',
      value: dataLoading ? '—' : metrics?.pendingReview?.toLocaleString(),
      icon: 'Clock',
      iconColor: 'bg-warning/10 text-warning',
    },
    {
      title: 'Total Registrations',
      value: dataLoading ? '—' : metrics?.totalRegistrations?.toLocaleString(),
      icon: 'Users',
      iconColor: 'bg-accent/10 text-accent',
    },
  ];

  return (
    <>
      <Sidebar />
      <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="flex items-start justify-between mb-6">
          <Breadcrumbs className="mb-0" items={[{ label: 'Dashboard', path: '/dashboard' }]} />
          <UserProfileDropdown user={{ name: 'Admin User', email: 'admin@kidquest.com' }} />
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back! Here's what's happening with your platform today.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            {dataLoading ? (
              <div className="card p-6 flex items-center justify-center h-48">
                <p className="text-muted-foreground text-sm">Loading recent events…</p>
              </div>
            ) : (
              <RecentContentTable content={recentEvents} />
            )}
          </div>
          <div>
            {dataLoading ? (
              <div className="card p-6 flex items-center justify-center h-48">
                <p className="text-muted-foreground text-sm">Loading activity…</p>
              </div>
            ) : (
              <ActivityFeed activities={activities} />
            )}
          </div>
        </div>

        <div className="mb-6">
          {dataLoading ? (
            <div className="card p-6 flex items-center justify-center h-48">
              <p className="text-muted-foreground text-sm">Loading chart…</p>
            </div>
          ) : (
            <PerformanceChart data={performanceData} />
          )}
        </div>

        <footer className="mt-12 pt-6 border-t border-border text-center text-sm text-muted-foreground">
          <p>&copy; {new Date()?.getFullYear()} KidQuest CMS. All rights reserved.</p>
        </footer>
      </div>
    </>
  );
};

const Dashboard = () => {
  return (
    <SidebarProvider>
      <DashboardContent />
    </SidebarProvider>
  );
};

export default Dashboard;